"use client";

import { Box, VStack, HStack, Text, Badge, Button } from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import {
  MOCK_ANALYSIS_RESULT,
  type AnalysisResult,
} from "../mockdata/analysisData";
import { getPriorityColor } from "../utils/helpers";
import { useAuthStore } from "@/_stores/useAuthStore";
import { Emoji } from "@/_components/ui/Emoji";

interface SuggestionTabProps {
  data: AnalysisResult;
  onSignupRequest: () => void;
}

export const SuggestionTab = ({
  data,
  onSignupRequest,
}: SuggestionTabProps) => {
  const t = useTranslations("resume_benchmark");
  const token = useAuthStore((s) => s.token);
  const previewImprovements = token
    ? data.improvements
    : MOCK_ANALYSIS_RESULT.improvements;

  return (
    <VStack gap={8} align="stretch">
      {/* Strengths */}
      <Box>
        <HStack gap={2} mb={4}>
          <Emoji emoji="sparkles" size={6} />
          <Text fontSize="lg" fontWeight="semibold" color="neutral.50">
            {t("suggestions.strengths")}
          </Text>
          <Badge
            px={2}
            py={0.5}
            rounded="full"
            fontSize="2xs"
            bg="green.900"
            color="green.300"
          >
            {t("suggestions.identifiedCount", {
              count: data.strengths.length,
            })}
          </Badge>
        </HStack>
        <VStack gap={3} align="stretch">
          {data.strengths.map((s, i) => (
            <Box
              key={i}
              p={4}
              rounded="xl"
              bg="rgba(34,197,94,0.06)"
              border="1px solid"
              borderColor="green.900"
              _hover={{ borderColor: "green.700" }}
              transition="all 0.2s"
            >
              <HStack gap={2} mb={1}>
                <Box
                  w={1.5}
                  h={1.5}
                  rounded="full"
                  bg="green.400"
                  flexShrink={0}
                  mt={0.5}
                />
                <Text fontSize="sm" fontWeight="semibold" color="green.300">
                  {s.title}
                </Text>
              </HStack>
              <Text
                fontSize="xs"
                color="whiteAlpha.600"
                pl={3.5}
                lineHeight="1.7"
              >
                {s.description}
              </Text>
            </Box>
          ))}
        </VStack>
      </Box>

      {/* Weaknesses */}
      <Box>
        <HStack gap={2} mb={4}>
          <Emoji emoji="police-car-light" size={6} />
          <Text fontSize="lg" fontWeight="semibold" color="neutral.50">
            {t("suggestions.areasToImprove")}
          </Text>
          <Badge
            px={2}
            py={0.5}
            rounded="full"
            fontSize="2xs"
            bg="red.900"
            color="red.300"
          >
            {t("suggestions.foundCount", { count: data.weaknesses.length })}
          </Badge>
        </HStack>
        <VStack gap={3} align="stretch">
          {data.weaknesses.map((w, i) => (
            <Box
              key={i}
              p={4}
              rounded="xl"
              bg="rgba(239,68,68,0.06)"
              border="1px solid"
              borderColor="red.900"
              _hover={{ borderColor: "red.700" }}
              transition="all 0.2s"
            >
              <HStack gap={2} mb={1}>
                <Box
                  w={1.5}
                  h={1.5}
                  rounded="full"
                  bg="red.400"
                  flexShrink={0}
                  mt={0.5}
                />
                <Text fontSize="sm" fontWeight="semibold" color="red.300">
                  {w.title}
                </Text>
              </HStack>
              <Text
                fontSize="xs"
                color="whiteAlpha.600"
                pl={3.5}
                lineHeight="1.7"
              >
                {w.description}
              </Text>
            </Box>
          ))}
        </VStack>
      </Box>

      {/* Improvements — blurred gate */}
      <Box position="relative">
        <HStack gap={2} mb={4}>
          <Emoji emoji="card-file-box" size={6} />
          <Text fontSize="lg" fontWeight="semibold" color="neutral.50">
            {t("suggestions.actionPlan")}
          </Text>
          <Badge
            px={2}
            py={0.5}
            rounded="full"
            fontSize="2xs"
            bg="primary.700"
            color="primary.100"
          >
            {t("suggestions.stepsCount", {
              count: previewImprovements.length,
            })}
          </Badge>
        </HStack>

        {/* Blurred content */}
        <Box position="relative" rounded="2xl" overflow="hidden" minH="300px">
          <VStack
            gap={3}
            align="stretch"
            filter={!token ? "blur(5px)" : "none"}
            pointerEvents={!token ? "none" : "auto"}
          >
            {previewImprovements.map((imp, i) => (
              <Box
                key={i}
                p={4}
                rounded="xl"
                bg="whiteAlpha.50"
                border="1px solid"
                borderColor="whiteAlpha.100"
                _hover={{ borderColor: "whiteAlpha.200", bg: "whiteAlpha.100" }}
              >
                <HStack gap={2} mb={2} justify="space-between">
                  <Text fontSize="sm" fontWeight="semibold" color="white">
                    {imp.section}
                  </Text>
                  <Badge
                    px={2}
                    py={0.5}
                    rounded="full"
                    fontSize="2xs"
                    textTransform="capitalize"
                    bg={`${getPriorityColor(imp.priority)}.900`}
                    color={`${getPriorityColor(imp.priority)}.300`}
                  >
                    {t(`priority.${imp.priority}`)}
                  </Badge>
                </HStack>
                <Text fontSize="xs" color="whiteAlpha.600" lineHeight="1.7">
                  {imp.suggestion}
                </Text>
              </Box>
            ))}
          </VStack>

          {/* Gate overlay */}
          {!token && (
            <Box
              position="absolute"
              boxSize="full"
              top={0}
              rounded="2xl"
              bg="linear-gradient(to top, rgba(9,9,18,0.98) 40%, rgba(9,9,18,0.6) 100%)"
              display="flex"
              alignItems="flex-end"
              justifyContent="center"
              pb={8}
            >
              <VStack gap={3} textAlign="center" px={6}>
                <Text fontSize="2xl">🔐</Text>
                <Text fontSize="sm" fontWeight="bold" color="white">
                  {t("suggestions.gate.title")}
                </Text>
                <Text fontSize="xs" color="whiteAlpha.500" maxW="320px">
                  {t("suggestions.gate.description", {
                    count: previewImprovements.length,
                  })}
                </Text>
                <Button
                  mt={1}
                  px={6}
                  py={2}
                  rounded="full"
                  bg="indigo.500"
                  color="white"
                  fontWeight="bold"
                  fontSize="sm"
                  _hover={{ bg: "indigo.400", transform: "translateY(-1px)" }}
                  transition="all 0.2s"
                  onClick={onSignupRequest}
                >
                  {t("suggestions.gate.cta")}
                </Button>
              </VStack>
            </Box>
          )}
        </Box>
      </Box>
    </VStack>
  );
};
